import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../common/dialog";
import Button from "../common/button";
import Input from "../common/input";
import { Label } from "../common/label";
import { Card } from "../common/card";
import { Badge } from "../common/badge";

interface SubscribeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planName: string;
  planPrice: number;
  billingCycle: "monthly" | "annual";
  walletBalance: number;
  durationDays?: number | string;
  mode?: "plan" | "addon";
  currencySymbol?: string;
  maxBonusPercent?: number;
  onConfirm: (bonusAmount: number) => void;
}

export function SubscribeModal({
  open,
  onOpenChange,
  planName,
  planPrice,
  billingCycle,
  walletBalance,
  durationDays,
  mode = "plan",
  currencySymbol,
  maxBonusPercent = 100,
  onConfirm,
}: SubscribeModalProps) {
  const [bonusAmount, setBonusAmount] = useState<number | null>(0);
  const [error, setError] = useState("");

  const currencyMark = getCurrencyMark(currencySymbol);
  const normalizedMaxBonusPercent = Math.max(0, Math.min(100, maxBonusPercent));
  const maxBonusByPercent = Math.round(planPrice * (normalizedMaxBonusPercent / 100));
  const maxBonusUsage = Math.max(0, Math.min(walletBalance, planPrice, maxBonusByPercent));
  const appliedBonus = Math.min(bonusAmount ?? 0, maxBonusUsage);
  const remainingToPay = Math.max(0, planPrice - appliedBonus);
  const bonusInputDisabled = maxBonusUsage === 0;

  useEffect(() => {
    if (open) {
      setBonusAmount(0);
      setError("");
    }
  }, [open]);

  const handleBonusAmountChange = (value: string) => {
    if (bonusInputDisabled) {
      return;
    }

    setError("");
    if (value === "") {
      setBonusAmount(null);
      return;
    }

    const numValue = Number(value);
    if (Number.isNaN(numValue)) {
      return;
    }

    if (numValue < 0) {
      setBonusAmount(0);
      setError("Сумма не может быть отрицательной");
      return;
    }

    const normalizedValue = Math.max(0, Math.floor(numValue));
    setBonusAmount(normalizedValue);

    if (normalizedValue > maxBonusUsage) {
      setError(`Максимум для оплаты бонусами — ${formatAmount(maxBonusUsage)} ${currencyMark}`);
    }
  };

  const handleBonusBlur = () => {
    if (bonusInputDisabled) {
      return;
    }

    if (bonusAmount === null) {
      setBonusAmount(0);
      return;
    }

    if (bonusAmount > maxBonusUsage) {
      setBonusAmount(maxBonusUsage);
      setError("");
    }
  };

  const handleUseMax = () => {
    if (!bonusInputDisabled) {
      setBonusAmount(maxBonusUsage);
      setError("");
    }
  };

  const handleConfirm = () => {
    if (error) {
      return;
    }

    onConfirm(appliedBonus);
  };

  const titleText = mode === "addon" ? "Подключение дополнения" : "Подключение тарифа";
  const durationLabel =
    typeof durationDays === "number" && durationDays > 0 ? `${durationDays} дней` : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-[0px_10px_30px_rgba(0,0,0,0.05)] font-comfortaa">
        <DialogHeader className="text-left">
          <DialogTitle className="text-[22px] font-comfortaa text-blackBase">
            {titleText}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Выберите способ оплаты для активации вашей подписки
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-3">
          {/* Plan Details */}
          <Card className="rounded-[18px] border border-[#E8E8E8] bg-grey5 p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-comfortaa !text-2xl !leading-tight font-semibold text-blackBase">
                  {planName}
                </h4>
              </div>
              {durationLabel && (
                <Badge className="bg-redBase text-white">
                  <span className="flex items-center gap-1">
                    <DurationIcon />
                    {durationLabel}
                  </span>
                </Badge>
              )}
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-3xl font-bold text-redBase">
                {formatAmount(planPrice)} {currencyMark}
              </span>
            </div>
          </Card>

          {/* Wallet Balance */}
          <div className="flex items-center gap-3 rounded-[18px] border border-[#EFEFEF] bg-grey5 p-4">
            <span className="flex size-10 items-center justify-center rounded-[12px] border border-[#EFEFEF] bg-white text-redBase">
              <WalletIcon />
            </span>
            <div>
              <p className="text-sm text-grey1">Бонусный баланс</p>
              <p className="text-lg font-semibold text-blackBase">
                {formatAmount(walletBalance)} {currencyMark}
              </p>
            </div>
          </div>

          {/* Bonus Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="bonus-amount" className="text-blackBase">
              {bonusInputDisabled
                ? "Бонусные рубли недоступны"
                : `Использовать бонусные рубли (макс. ${formatAmount(maxBonusUsage)} ${currencyMark})`}
            </Label>
            <div className="w-full">
              <Input
                id="bonus-amount"
                type="number"
                min={0}
                max={maxBonusUsage}
                value={bonusAmount ?? ""}
                onChange={(e) => handleBonusAmountChange(e.target.value)}
                onBlur={handleBonusBlur}
                placeholder="0"
                disabled={bonusInputDisabled}
                error={error}
                className="!rounded-[12px] !border-transparent !bg-grey5 !text-lg !pr-[88px]"
                icon={{
                  type: "suffix",
                  icon: (
                    <div className="flex items-center gap-2">
                      <span className="text-grey2">{currencyMark}</span>
                      <button
                        type="button"
                        onClick={handleUseMax}
                        disabled={bonusInputDisabled}
                        className="rounded-[8px] border border-[#E0E0E0] bg-white px-2 py-1 text-xs text-grey2 hover:text-blackBase disabled:opacity-50"
                      >
                        Макс
                      </button>
                    </div>
                  ),
                }}
              />
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="space-y-3 rounded-[18px] border border-[#EFEFEF] bg-grey5 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-grey1">Стоимость подписки</span>
              <span className="text-blackBase">
                {formatAmount(planPrice)} {currencyMark}
              </span>
            </div>
            {appliedBonus > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-grey1">Бонусные рубли</span>
                <span className="text-green-600">
                  -{formatAmount(appliedBonus)} {currencyMark}
                </span>
              </div>
            )}
            <div className="h-px bg-[#D9D9D9]" />
            <div className="flex items-center justify-between text-sm">
              <span className="text-blackBase font-semibold">К оплате</span>
              <span className="text-2xl font-semibold text-blackBase">
                {formatAmount(remainingToPay)} {currencyMark}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-3 sm:gap-4">
          <Button
            type="grey"
            className="!w-auto !h-[40px] !px-5 !py-0 !text-sm md:!text-base !items-center"
            onClick={() => onOpenChange(false)}
          >
            Отмена
          </Button>
          <Button
            type="black"
            className="!w-auto !h-[40px] !px-6 !py-0 !text-sm md:!text-base !items-center"
            onClick={handleConfirm}
            disabled={!!error}
          >
            {remainingToPay > 0
              ? `Оплатить ${formatAmount(remainingToPay)} ${currencyMark}`
              : "Активировать подписку"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const formatAmount = (value: number) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(Math.max(0, Math.round(value)));

const getCurrencyMark = (currencySymbol?: string) => {
  if (!currencySymbol) return "₽";

  const normalized = currencySymbol.toUpperCase().trim();

  if (["RUB", "RUR", "₽", "РУБ"].includes(normalized)) return "₽";
  if (["USD", "$"].includes(normalized)) return "$";
  if (["EUR", "€"].includes(normalized)) return "€";

  return currencySymbol;
};

const WalletIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-5" aria-hidden="true">
    <rect x="3" y="6" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="1.5" />
    <path d="M17 10h4v6h-4a2 2 0 0 1 0-6Z" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="17" cy="13" r="1" fill="currentColor" />
  </svg>
);

const DurationIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-3" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 7v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
